import { useMemo, useState } from 'react';

import {
  type Item,
  type ItemVariantAxisOptions,
  useItemVariantAxesFor,
  useItems,
  useVendors,
} from '@/api/masters';
import { UnitConditionValues, type UnitCondition } from '@/api/generated/enums';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass, selectClass } from '@/components/Field';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { Button, Card, Notice } from '@/components/PageHeading';
import { toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { humanizeEnum } from '@core/format/text';
import {
  addLine,
  draftProblems,
  draftTotals,
  emptyDraft,
  parseSerials,
  removeLine,
  setLineAxis,
  setLineItem,
  updateLine,
  type IntakeDraft,
  type IntakeLine,
} from './intake';

/**
 * Booking in a delivery — several designs, on one page.
 *
 * <p>
 * <strong>This replaces a drawer that took one configuration at a time, and the change is not
 * cosmetic.</strong> A consignment is four navy 40s, two navy 42s and a pair of shoes off one
 * invoice. Through the drawer that was three round trips: open, fill, save, reopen, and the purchase
 * date and the supplier note retyped each time. The lines now sit in one grid with the totals pinned
 * beside them, which is the shape the template already uses for a purchase order — and the same
 * shape `NewBookingPage` took for the counter.
 * </p>
 *
 * <p>
 * <strong>The axis selectors are the interesting part.</strong> They are not fixed columns: they
 * come from each line's own design and change the moment the line names a different one. That is
 * the category's attribute schema doing its job — one grid serves sherwanis varying by Size ×
 * Colour and cufflinks varying by nothing, without a line of per-garment-type code.
 * </p>
 *
 * <p>
 * `intake.ts` is the whole state — plain data with pure functions over it — so what the screen will
 * post, and why it refuses to, are decided somewhere that can be read and tested on its own.
 * </p>
 */
export function StockIntakePage({
  today,
  items,
  branchId,
  branchName,
  currency = '',
  busy = false,
  initial,
  against,
  onClose,
  onSubmit,
}: {
  today: string;
  /** A delivery to start from — Receive on a purchase order passes what is still to come. */
  initial?: IntakeDraft | undefined;
  /**
   * The order this delivery is received against. Its supplier is fixed and each of its lines keeps
   * the order's design and size; only how many came and their cost change.
   */
  against?: { orderNumber: string; supplierName: string } | undefined;
  /** The catalogue to file garments against, inactive designs included. */
  items: Item[];
  /** Whose catalogue the lines search. */
  branchId: number | undefined;
  branchName: string;
  currency?: string;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (draft: IntakeDraft) => void;
}) {
  const [draft, setDraft] = useState<IntakeDraft>(() => initial ?? emptyDraft(today));

  // The server refuses a vendor not marked as a supplier, so only those are offered.
  const [supplier, setSupplier] = useState<PickerOption | null>(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const suppliers = useVendors(branchId, {
    capability: 'Supplier',
    search: supplierSearch,
    pageSize: 10,
  });
  const supplierOptions: PickerOption[] = (suppliers.data?.items ?? []).map((vendor) => ({
    id: String(vendor.id),
    primary: vendor.name,
    secondary: vendor.mobile,
  }));

  const itemIds = useMemo(
    () => draft.lines.map((line) => line.itemId).filter((id): id is number => id !== null),
    [draft.lines],
  );

  const { axes, isLoading: axesLoading } = useItemVariantAxesFor(itemIds);

  const totals = draftTotals(draft);
  const problems = draftProblems(draft, axes);

  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  // Lands on the purchase date, which is the top of the delivery and where a storeman reading off
  // an invoice starts. Enter walks from there into the lines.
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  // A line with no design named is a blank row the storeman has not reached yet, not an error, so
  // it neither counts towards the delivery nor blocks it — but a delivery of nothing but blanks has
  // nothing to post.
  const empty = totals.units === 0;

  return (
    <>
      {/* -------------------------------------------------------------- header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to the rail"
            className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
          >
            <i className="icon icon-arrow-left" />
          </button>
          <div>
            <h1 className="page-title">
              {against ? `Receive against ${against.orderNumber}` : 'Book in stock'}
            </h1>
            <p className="page-subtitle">
              {against
                ? `What is still to come is filled in. Change how many came and their cost; remove a line that did not come. Everything lands at ${branchName}.`
                : `One line per configuration. Everything here lands at ${branchName} — a garment hangs on one rail.`}
            </p>
          </div>
        </div>
      </div>

      {/*
        Enter moves to the next field and the last one hands focus to "Book in" — see
        `enterKeyNavigation`. A storeman keys a delivery off a paper invoice with one hand on the
        number pad, which is the case that rule was written for; this screen having no `<form>` is
        why the handler sits on the grid instead.
      */}
      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        {/* ----------------------------------------------------- left column */}
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
          <Card>
            <SectionTitle>Delivery</SectionTitle>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Purchased on" hint="Applies to every line — one consignment, one date.">
                {/* The app's picker rather than `type="date"`: the native control renders in the
                    browser's own locale, so it could not follow the company's chosen format and read
                    differently from every date on the units list behind it. */}
                <DatePicker
                  className="w-full"
                  value={toDayjs(draft.purchasedOn) ?? null}
                  disabled={busy}
                  onChange={(value) =>
                    setDraft((was) => ({ ...was, purchasedOn: toIsoDate(value) ?? '' }))
                  }
                />
              </Field>

              <div className="sm:col-span-2">
                <Field
                  label="Supplier"
                  hint="Who this delivery was bought from. Only vendors marked as a supplier are listed."
                >
                  {/* Kept as the picked option rather than looked up in the current results, so
                      typing a new search does not blank a supplier already chosen. Fixed when
                      receiving against an order: the server refuses any other supplier. */}
                  {against ? (
                    <input value={against.supplierName} disabled className={controlClass} />
                  ) : (
                    <SearchPicker
                      value={supplier}
                      options={supplierOptions}
                      total={suppliers.data?.total ?? 0}
                      loading={suppliers.isLoading}
                      disabled={busy}
                      placeholder="Search suppliers"
                      onSearch={setSupplierSearch}
                      onSelect={(option) => {
                        setSupplier(option);
                        setDraft((was) => ({
                          ...was,
                          supplierVendorId: option === null ? null : Number(option.id),
                        }));
                      }}
                    />
                  )}
                </Field>
              </div>

              <Field label="Their challan / invoice no." hint="As printed on the supplier's paper.">
                <input
                  value={draft.supplierReference}
                  disabled={busy}
                  onChange={(event) =>
                    setDraft((was) => ({ ...was, supplierReference: event.target.value }))
                  }
                  placeholder="4471"
                  className={controlClass}
                />
              </Field>

              <div className="sm:col-span-2">
                <Field label="Notes" hint="Anything worth recording about the delivery.">
                  <input
                    value={draft.notes}
                    disabled={busy}
                    onChange={(event) => setDraft((was) => ({ ...was, notes: event.target.value }))}
                    className={controlClass}
                  />
                </Field>
              </div>
            </div>
          </Card>

          {/* ---- the lines ---- */}
          <Card>
            <SectionTitle
              trailing={
                <span className="text-default text-[12px]">
                  {draft.lines.length} line{draft.lines.length === 1 ? '' : 's'} · {totals.units}{' '}
                  garment{totals.units === 1 ? '' : 's'}
                </span>
              }
            >
              Garments
            </SectionTitle>

            <div className="flex flex-col gap-3">
              {draft.lines.map((line, index) => (
                <LineRow
                  key={line.key}
                  index={index}
                  line={line}
                  branchId={branchId}
                  item={line.itemId === null ? undefined : itemsById.get(line.itemId)}
                  axes={line.itemId === null ? undefined : axes[line.itemId]}
                  currency={currency}
                  busy={busy}
                  removable={draft.lines.length > 1 || line.itemId !== null}
                  orderNumber={line.purchaseOrderLineId === null ? undefined : against?.orderNumber}
                  onChange={(changes) => setDraft((was) => updateLine(was, line.key, changes))}
                  onItem={(itemId) => setDraft((was) => setLineItem(was, line.key, itemId))}
                  onAxis={(attributeId, valueId) =>
                    setDraft((was) => setLineAxis(was, line.key, attributeId, valueId))
                  }
                  onRemove={() => setDraft((was) => removeLine(was, line.key))}
                />
              ))}
            </div>

            <button
              type="button"
              disabled={busy}
              onClick={() => setDraft(addLine)}
              className="border-border-color text-default mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded border bg-light px-2.5 py-1.5 text-[12px] font-medium hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed"
            >
              <i className="icon icon-plus" />
              Add another line
            </button>
          </Card>
        </div>

        {/* ---------------------------------------------------- right column */}
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card>
            <SectionTitle>Delivery summary</SectionTitle>

            {empty ? (
              <p className="text-default py-6 text-center text-[12.5px]">
                Nothing to book in yet — name a design on the first line.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {draft.lines.map((line, index) =>
                  line.itemId === null ? null : (
                    <p
                      key={line.key}
                      className="text-default flex justify-between gap-2 text-[12px]"
                    >
                      <span className="truncate">
                        {index + 1}. {itemsById.get(line.itemId)?.name ?? 'Item'} × {line.quantity}
                      </span>
                      <span className="font-mono-nums shrink-0 text-gray-900">
                        {line.purchaseCost === null
                          ? '—'
                          : `${currency}${formatAmount(line.purchaseCost * line.quantity)}`}
                      </span>
                    </p>
                  ),
                )}
              </div>
            )}

            <div className="border-border-color mt-3 border-t pt-3">
              <Row label="Garments" value={String(totals.units)} strong />
              <Row
                label="Cost of the delivery"
                value={`${currency}${formatAmount(totals.cost)}`}
                tone="brand"
              />
              <p className="text-default mt-1 text-[11px]">
                Cost is recorded per garment, which is what Section 20&apos;s payback is worked out
                from. Lines with no cost are booked in without one.
              </p>
            </div>
          </Card>

          {problems.length > 0 && (
            <Notice tone="warning" title="Not ready to book in">
              <ul className="mt-1 flex list-disc flex-col gap-0.5 ps-4">
                {problems.slice(0, 6).map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
                {problems.length > 6 && <li>…and {problems.length - 6} more.</li>}
              </ul>
            </Notice>
          )}

          <Card>
            <p className="text-default mb-3 text-[12px]">
              All or nothing: if any line is refused, none of them are written. Serials are
              generated from each configuration&apos;s code unless the garments already carry tags.
            </p>

            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                // Loading axes is not a refusal, but submitting through it would post lines whose
                // requirements nobody has seen yet.
                disabled={busy || empty || problems.length > 0 || axesLoading}
                onClick={() => onSubmit(draft)}
              >
                {busy
                  ? 'Booking in…'
                  : `Book in ${totals.units} garment${totals.units === 1 ? '' : 's'}`}
              </Button>

              <Button variant="secondary" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * One line of the delivery.
 *
 * <p>
 * A card rather than a `<tr>`, which is a departure from the template's purchase-order grid and a
 * deliberate one: every row there has the same columns, and here the configuration cell holds a
 * different number of selectors per line. A table whose cells reflow to different heights per row
 * reads as broken, and on a phone `style.css` puts `text-nowrap` on every `td` so it would not
 * reflow at all — the same reason `DataView` exists.
 * </p>
 */
function LineRow({
  index,
  line,
  branchId,
  item,
  axes,
  currency,
  busy,
  removable,
  onChange,
  onItem,
  onAxis,
  onRemove,
  orderNumber,
}: {
  index: number;
  line: IntakeLine;
  /** Set on a line from a purchase order: its design and size are the order's and locked. */
  orderNumber?: string | undefined;
  item: Item | undefined;
  axes: ItemVariantAxisOptions[] | undefined;
  currency: string;
  busy: boolean;
  removable: boolean;
  onChange: (changes: Partial<Omit<IntakeLine, 'key'>>) => void;
  onItem: (itemId: number | null) => void;
  onAxis: (attributeId: number, valueId: number) => void;
  onRemove: () => void;
  /** Whose catalogue to search. */
  branchId: number | undefined;
}) {
  /*
    Each line searches for itself, because each line is a different design. The page keeps its own
    `useItems` for the summary — it has to name a chosen design without waiting on any line's
    query — and React Query shares the cache between them where the arguments match.
  */
  const [search, setSearch] = useState('');

  const matches = useItems(branchId, { search, pageSize: 10 });

  const options: PickerOption[] = (matches.data?.items ?? []).map((option) => ({
    id: String(option.id),
    primary: option.name,
    secondary: option.code,

    // No badge here, deliberately. The Item column is 4 of 12 and a category beside the name costs
    // the name — "Gold Zari Sherw…" — which is the one thing that has to be readable. The category
    // is also the least useful fact on this row: you have just searched the catalogue, and the code
    // below identifies the design outright.
  }));

  const serials = parseSerials(line.serialCodes);

  return (
    <div className="border-border-color rounded border bg-light p-3">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-gray-900">
          Line {index + 1}
          {orderNumber && <span className="text-default ms-2 font-normal">from {orderNumber}</span>}
        </span>

        {removable && (
          <button
            type="button"
            disabled={busy}
            onClick={onRemove}
            aria-label={`Remove line ${index + 1}`}
            className="text-default flex size-7 cursor-pointer items-center justify-center rounded border border-transparent hover:border-border-color hover:bg-white hover:text-danger disabled:cursor-not-allowed"
          >
            <i className="icon icon-trash-2 text-[13px]" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
        <div className="sm:col-span-4">
          <Field label="Item">
            {/*
              Searched rather than listed. The catalogue is the one list here that genuinely runs to
              hundreds — the page fetches 200 and a chain with more than that could not reach the
              rest at all — and scrolling a native select to find "Gold Zari Sherwani" with a
              delivery on the counter is the wrong shape for the job.
            */}
            <SearchPicker
              value={
                item ? { id: String(item.id), primary: item.name, secondary: item.code } : null
              }
              options={options}
              total={matches.data?.total ?? 0}
              loading={matches.isLoading}
              disabled={busy || orderNumber !== undefined}
              placeholder="Search designs"
              onSearch={setSearch}
              onSelect={(option) => onItem(option === null ? null : Number(option.id))}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="How many">
            <input
              type="number"
              onWheel={(event) => event.currentTarget.blur()}
              min={1}
              max={100}
              value={line.quantity}
              disabled={busy}
              onChange={(event) => onChange({ quantity: Number(event.target.value) })}
              className={controlClass}
            />
          </Field>
        </div>

        <div className="sm:col-span-3">
          <Field label={`Cost per garment${currency ? ` (${currency})` : ''}`}>
            <input
              type="number"
              onWheel={(event) => event.currentTarget.blur()}
              min={0}
              step="0.01"
              value={line.purchaseCost ?? ''}
              disabled={busy}
              onChange={(event) =>
                onChange({
                  purchaseCost: event.target.value === '' ? null : Number(event.target.value),
                })
              }
              placeholder="Leave blank if unknown"
              className={controlClass}
            />
          </Field>
        </div>

        {/*
          What the shop *charges*, beside what it paid — and the reason this screen is no longer a
          dead end. Stock booked in without a rate somewhere above it cannot be booked out at all,
          so before this the storeman finished here and the shop still could not take a wedding.

          Deliberately one number rather than the four decisions `/rate-cards` asks for. A shop with
          a duration ladder leaves it blank and keeps the ladder; blank is silence, not a withdrawal.
        */}
        <div className="sm:col-span-3">
          <Field
            label={`Price per day${currency ? ` (${currency})` : ''}`}
            hint="Leave blank if a rate card already prices this design."
          >
            <input
              type="number"
              onWheel={(event) => event.currentTarget.blur()}
              min={0}
              step="0.01"
              value={line.rentPerDay ?? ''}
              disabled={busy}
              onChange={(event) =>
                onChange({
                  rentPerDay: event.target.value === '' ? null : Number(event.target.value),
                })
              }
              placeholder="What it rents for"
              className={controlClass}
            />
          </Field>
        </div>
      </div>

      {/* ---- the configuration, which is data rather than fixed fields ---- */}
      {line.itemId !== null && (
        <div className="mt-3">
          {axes === undefined ? (
            <p className="text-default text-[12px]">Loading this item&apos;s options…</p>
          ) : axes.length === 0 ? (
            <Notice tone="warning" title="This item's category has no variant-forming attributes">
              Garments of {item?.name ?? 'this design'} cannot be told apart yet. Add an attribute
              to its category and mark it variant-forming — Size and Colour are the usual pair.
            </Notice>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {axes.map((axis) => (
                <Field key={axis.attributeId} label={axis.attributeName}>
                  <select
                    value={line.axisValues[axis.attributeId] ?? ''}
                    disabled={busy || orderNumber !== undefined}
                    onChange={(event) => onAxis(axis.attributeId, Number(event.target.value))}
                    className={selectClass}
                  >
                    <option value="">Choose {axis.attributeName.toLowerCase()}</option>
                    {axis.values.map((value) => (
                      <option key={value.valueId} value={value.valueId}>
                        {value.rateAdjustmentPct
                          ? `${value.name} (${value.rateAdjustmentPct > 0 ? '+' : ''}${value.rateAdjustmentPct}%)`
                          : value.name}
                      </option>
                    ))}
                  </select>
                </Field>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- condition and tags ---- */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-12">
        <div className="sm:col-span-4">
          <Field label="Condition">
            <select
              value={line.condition}
              disabled={busy}
              onChange={(event) => onChange({ condition: event.target.value as UnitCondition })}
              className={selectClass}
            >
              {UnitConditionValues.map((value) => (
                <option key={value} value={value}>
                  {humanizeEnum(value)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="sm:col-span-8">
          <span className="mb-1.5 block text-[12px] font-medium text-gray-900">Serial codes</span>

          <label className="text-default mb-1.5 inline-flex cursor-pointer items-center gap-2 text-[12px]">
            <input
              type="checkbox"
              checked={line.ownSerials}
              disabled={busy}
              onChange={(event) => onChange({ ownSerials: event.target.checked })}
              className="accent-primary size-3.5 cursor-pointer"
            />
            These garments already have tags
          </label>

          {line.ownSerials ? (
            <>
              <textarea
                rows={2}
                value={line.serialCodes}
                disabled={busy}
                onChange={(event) => onChange({ serialCodes: event.target.value })}
                placeholder="One per line, or comma separated"
                className="border-border-color w-full rounded border px-2.5 py-1.5 text-[13px] disabled:cursor-not-allowed disabled:bg-gray-50"
              />
              <span className="text-default mt-1 block text-[11.5px]">
                {/* All or nothing, which the server also enforces: a batch half-generated and
                    half-transcribed is one the storeman cannot tell apart afterwards. */}
                {serials.length} of {line.quantity} given.
              </span>
            </>
          ) : (
            <p className="text-default text-[11.5px]">
              {line.quantity} serial{line.quantity === 1 ? '' : 's'} will be generated from the
              configuration&apos;s code, continuing from the highest already issued.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function SectionTitle({
  children,
  trailing,
}: {
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-sm font-semibold text-gray-900">{children}</h2>
      {trailing}
    </div>
  );
}

// See `components/Field.tsx` — this screen's private copy was one of four that had drifted apart.

function Row({
  label,
  value,
  strong = false,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'brand';
}) {
  const colour = tone === 'brand' ? 'text-primary' : 'text-gray-900';

  return (
    <p className="flex items-baseline justify-between gap-3 py-1 text-[13px]">
      <span className="text-default">{label}</span>
      <span
        className={`font-mono-nums ${colour} ${strong ? 'text-[15px] font-semibold' : 'font-medium'}`}
      >
        {value}
      </span>
    </p>
  );
}
